import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";

/**
 * A page title that stays at the top while the page scrolls. It publishes its own height as --rc-sticky-title-h so a
 * sticky table header (.rc-sticky-head in styles/responsive.css) can sit directly under it, however tall the title is
 * (a wrapped subtitle, a larger font). Use one per page.
 */
export function StickyTitle({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty("--rc-sticky-title-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(publish) : null;
    observer?.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", publish);
      root.style.removeProperty("--rc-sticky-title-h");
    };
  }, []);
  return (
    <div ref={ref} className="rc-sticky-title" style={style}>
      {children}
    </div>
  );
}
